<script setup>
import { ref, computed } from 'vue';
import { formatNumber } from '@chatwoot/utils';
import { vOnClickOutside } from '@vueuse/components';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { getChannelColor } from 'next/icon/provider';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  inboxes: { type: Array, default: () => [] },
  labels: { type: Array, default: () => [] },
  folders: { type: Array, default: () => [] },
  activeInboxId: { type: [String, Number], default: 0 },
  activeLabel: { type: String, default: '' },
  activeFolderId: { type: [String, Number], default: 0 },
  showNonService: { type: Boolean, default: false },
  showGroupsOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'inboxSelect',
  'labelSelect',
  'folderSelect',
  'resetView',
  'toggleNonService',
  'toggleGroups',
]);

const showInboxDropdown = ref(false);
const showLabelDropdown = ref(false);
const showFolderDropdown = ref(false);

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const hasAnyActiveFilter = computed(() => {
  return (
    Number(props.activeInboxId) > 0 ||
    !!props.activeLabel ||
    props.hasActiveFolders ||
    props.hasAppliedFilters
  );
});

const closeAllDropdowns = () => {
  showInboxDropdown.value = false;
  showLabelDropdown.value = false;
  showFolderDropdown.value = false;
};

const toggleInboxDropdown = () => {
  const wasOpen = showInboxDropdown.value;
  closeAllDropdowns();
  showInboxDropdown.value = !wasOpen;
};

const toggleLabelDropdown = () => {
  const wasOpen = showLabelDropdown.value;
  closeAllDropdowns();
  showLabelDropdown.value = !wasOpen;
};

const toggleFolderDropdown = () => {
  const wasOpen = showFolderDropdown.value;
  closeAllDropdowns();
  showFolderDropdown.value = !wasOpen;
};

const selectInbox = inbox => {
  showInboxDropdown.value = false;
  emit('inboxSelect', inbox);
};

const selectLabel = label => {
  showLabelDropdown.value = false;
  emit('labelSelect', label);
};

const isActiveInbox = inbox => {
  return Number(props.activeInboxId) === inbox.id;
};

const isActiveLabel = label => {
  return props.activeLabel === label.title;
};

const selectFolder = folder => {
  showFolderDropdown.value = false;
  emit('folderSelect', folder);
};

const isActiveFolder = folder => {
  return Number(props.activeFolderId) === folder.id;
};

const inboxIcon = inbox => {
  return getInboxIconByType(inbox.channel_type, inbox.medium, 'line');
};

const inboxColorClass = inbox => {
  return getChannelColor(inbox.channel_type);
};
</script>

<template>
  <div
    class="flex items-center justify-between px-3.5 py-2"
    :class="{
      'border-b border-n-stone-100': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <!-- Left: Context badge -->
    <div class="flex items-center gap-1.5 min-w-0">
      <span
        v-if="hasAppliedFiltersOrActiveFolders"
        class="chatlist-badge"
        :title="pageTitle"
      >
        {{ pageTitle }}
      </span>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="chatlist-badge"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span v-if="!hasAppliedFiltersOrActiveFolders" class="chatlist-badge">
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>

    <!-- Right: Actions -->
    <div class="flex items-center gap-0.5">
      <!-- Home / Reset view -->
      <NextButton
        v-if="hasAnyActiveFilter"
        v-tooltip.top-end="'Todas as conversas'"
        icon="i-lucide-house"
        slate
        xs
        faded
        @click="emit('resetView')"
      />

      <!-- Inbox shortcut -->
      <div
        v-if="inboxes.length > 0"
        v-on-click-outside="() => (showInboxDropdown = false)"
        class="relative"
      >
        <NextButton
          v-tooltip.top-end="'Caixas de entrada'"
          icon="i-lucide-inbox"
          slate
          xs
          faded
          :class="{ '!bg-n-brand-50 !text-n-brand': activeInboxId > 0 }"
          @click="toggleInboxDropdown"
        />
        <div
          v-if="showInboxDropdown"
          class="dropdown-panel ltr:right-0 rtl:left-0"
        >
          <button
            class="dropdown-item"
            :class="{ active: !activeInboxId || activeInboxId == 0 }"
            @click="selectInbox(null)"
          >
            <span class="i-lucide-layers size-3.5 text-n-stone-300" />
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span>Todas</span>
          </button>
          <div class="h-px bg-n-stone-100 my-1" />
          <button
            v-for="inbox in inboxes"
            :key="inbox.id"
            class="dropdown-item"
            :class="{ active: isActiveInbox(inbox) }"
            @click="selectInbox(inbox)"
          >
            <span :class="[inboxIcon(inbox), inboxColorClass(inbox)]" class="size-3.5" />
            <span class="truncate">{{ inbox.name }}</span>
          </button>
        </div>
      </div>

      <!-- Non-service inbox conversations toggle -->
      <NextButton
        v-tooltip.top-end="$t('CHAT_LIST.NON_SERVICE_INBOXES')"
        icon="i-lucide-archive"
        slate
        xs
        faded
        :class="{ '!bg-n-brand-50 !text-n-brand': showNonService }"
        @click="emit('toggleNonService')"
      />

      <!-- Label shortcut -->
      <div
        v-if="labels.length > 0"
        v-on-click-outside="() => (showLabelDropdown = false)"
        class="relative"
      >
        <NextButton
          v-tooltip.top-end="'Etiquetas'"
          icon="i-lucide-tag"
          slate
          xs
          faded
          :class="{ '!bg-n-brand-50 !text-n-brand': activeLabel }"
          @click="toggleLabelDropdown"
        />
        <div
          v-if="showLabelDropdown"
          class="dropdown-panel ltr:right-0 rtl:left-0"
        >
          <button
            class="dropdown-item"
            :class="{ active: !activeLabel }"
            @click="selectLabel(null)"
          >
            <span class="i-lucide-layers size-3.5 text-n-stone-300" />
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span>Todas</span>
          </button>
          <div class="h-px bg-n-stone-100 my-1" />
          <button
            v-for="label in labels"
            :key="label.id"
            class="dropdown-item"
            :class="{ active: isActiveLabel(label) }"
            @click="selectLabel(label)"
          >
            <span
              class="size-2.5 rounded-full flex-shrink-0"
              :style="{ background: label.color }"
            />
            <span class="truncate">{{ label.title }}</span>
          </button>
        </div>
      </div>

      <!-- Filter/Folder actions -->
      <template v-if="hasAppliedFilters && !hasActiveFolders">
        <div class="relative">
          <NextButton
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            icon="i-lucide-save"
            slate
            xs
            faded
            @click="emit('addFolders')"
          />
          <div
            id="saveFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
          icon="i-lucide-circle-x"
          ruby
          faded
          xs
          @click="emit('resetFilters')"
        />
      </template>
      <template v-if="hasActiveFolders">
        <div class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            icon="i-lucide-pen-line"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
          icon="i-lucide-trash-2"
          ruby
          xs
          faded
          @click="emit('deleteFolders')"
        />
      </template>
      <div v-else class="relative">
        <NextButton
          id="toggleConversationFilterButton"
          v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
          icon="i-lucide-list-filter"
          slate
          xs
          faded
          @click="emit('filtersModal')"
        />
        <div
          id="conversationFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
        />
      </div>

      <!-- Folder shortcut -->
      <div
        v-if="folders.length > 0 && !hasAppliedFiltersOrActiveFolders"
        v-on-click-outside="() => (showFolderDropdown = false)"
        class="relative"
      >
        <NextButton
          v-tooltip.top-end="'Pastas'"
          icon="i-lucide-folder"
          slate
          xs
          faded
          :class="{ '!bg-n-brand-50 !text-n-brand': activeFolderId > 0 }"
          @click="toggleFolderDropdown"
        />
        <div
          v-if="showFolderDropdown"
          class="dropdown-panel ltr:right-0 rtl:left-0"
        >
          <button
            class="dropdown-item"
            :class="{ active: !activeFolderId || activeFolderId == 0 }"
            @click="selectFolder(null)"
          >
            <span class="i-lucide-layers size-3.5 text-n-stone-300" />
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            <span>Todas</span>
          </button>
          <div class="h-px bg-n-stone-100 my-1" />
          <button
            v-for="folder in folders"
            :key="folder.id"
            class="dropdown-item"
            :class="{ active: isActiveFolder(folder) }"
            @click="selectFolder(folder)"
          >
            <span class="i-lucide-folder size-3.5" />
            <span class="truncate">{{ folder.name }}</span>
          </button>
        </div>
      </div>

      <ConversationBasicFilter
        v-if="!hasAppliedFiltersOrActiveFolders"
        :is-on-expanded-layout="isOnExpandedLayout"
        @change-filter="onBasicFilterChange"
      />

      <!-- Separator + Groups toggle -->
      <div class="w-px h-4 bg-n-slate-4 mx-0.5" />
      <NextButton
        v-tooltip.top-end="$t('CHAT_LIST.GROUPS_FILTER')"
        icon="i-lucide-users"
        slate
        xs
        faded
        :class="{ '!bg-n-teal-3 !text-n-teal-11': showGroupsOnly }"
        @click="emit('toggleGroups')"
      />
    </div>
  </div>
</template>

<style scoped>
.chatlist-badge {
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--stone-300));
  background: rgb(var(--stone-50));
  padding: 2px 8px;
  border-radius: 10px;
  flex-shrink: 0;
  text-transform: capitalize;
}

.dropdown-panel {
  position: absolute;
  z-index: 40;
  top: 100%;
  margin-top: 4px;
  width: 220px;
  max-height: 280px;
  overflow-y: auto;
  background: rgb(var(--stone-25));
  backdrop-filter: blur(100px);
  border: 1px solid rgb(var(--stone-100));
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 12px;
  color: rgb(var(--stone-500));
  cursor: pointer;
  transition: background 0.15s;
  text-align: left;
  border: none;
  background: transparent;
}

.dropdown-item:hover {
  background: rgb(var(--stone-100));
}

.dropdown-item.active {
  background: rgb(var(--brand-50));
  color: rgb(var(--brand-primary));
  font-weight: 600;
}
</style>
